import React, { FC, useMemo } from 'react';
import styles from './CheckoutPage.module.css';
import { OrderStoreImplementation } from 'Data/Order/OrderStoreImplementation';
import { formatMoney } from 'Logic/Functions/UtilsFunctions';
import useImageUploadRules from 'Logic/Functions/Hooks/useImageUploadRules';
import CheckoutSummaryCard from './CheckoutSummaryCard';
import { OrderService } from 'Logic/OrderService';

const CheckoutSummarySection: FC = () => {
  const { orderState } = OrderStoreImplementation();
  const orderItems = orderState.items ?? [];
  const selectedDeliveryCarrier = orderState.selectedDeliveryCarrier ?? null;
  const deliveryFeeAmount = Number(selectedDeliveryCarrier?.fees ?? 0) || 0;
  const orderService = new OrderService();

  const { buildImageSrc } = useImageUploadRules({
    mode: 'image',
    maxFiles: 1,
    maxSizeMB: 5,
    requireAtLeastOne: false,
    enforceTotalSize: false,
  });

  const { subtotal: itemsSubtotal, grandTotal: orderGrandTotal } = useMemo(
    () =>
      orderService.computeGrandTotal({
        items: orderItems,
        deliveryFee: deliveryFeeAmount,
      }),
    [orderItems, deliveryFeeAmount]
  );

  return (
    <section className={styles.section} data-testid="checkout-summary-section">
      <div className={styles.sectionBody}>
        <div className={styles.receiptWrap} data-testid="checkout-summary-receipt">
          <div className={styles.receiptTearTop} aria-hidden="true" />

          <div className={styles.receiptPaper}>
            <header className={styles.sectionHeader}>
              <h3 className={styles.sectionTitle}>Summary</h3>
            </header>

            <div className={styles.receiptHeader}>
              <div className={styles.receiptHeaderLeft}>
                <div className={styles.receiptBrand}>ChimeraTech Store</div>
                <div className={styles.receiptSubTitle}>Order summary receipt</div>

                {selectedDeliveryCarrier?.name ? (
                  <div className={styles.receiptSubTitle}>
                    Delivery carrier: {selectedDeliveryCarrier.name}
                    {selectedDeliveryCarrier.deliveryTimeEstimation
                      ? ` • ${selectedDeliveryCarrier.deliveryTimeEstimation}`
                      : ''}
                  </div>
                ) : null}
              </div>

              <div className={styles.receiptHeaderRight}>
                <div className={styles.receiptBadge}>
                  {orderItems.length} item{orderItems.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>

            <div className={styles.receiptDividerDotted} />

            <div className={styles.receiptListHeader}>
              <span className={styles.receiptListHeaderLeft}>Item</span>
              <span className={styles.receiptListHeaderRight}>Total</span>
            </div>

            <ul className={styles.receiptList} data-testid="checkout-summary-list">
              {orderItems.map((orderItem) => (
                <CheckoutSummaryCard
                  key={orderItem.id}
                  item={orderItem}
                  imageSrc={buildImageSrc(orderItem.image ?? null)}
                />
              ))}
            </ul>

            <div className={styles.receiptDividerDotted} />

            <div className={styles.receiptTotals}>
              <div className={styles.receiptTotalRow}>
                <span className={styles.receiptTotalLabel}>Subtotal</span>
                <span className={styles.receiptTotalValue}>{formatMoney(itemsSubtotal)}</span>
              </div>

              <div className={styles.receiptTotalRow}>
                <span className={styles.receiptTotalLabel}>Delivery</span>
                <span className={styles.receiptTotalValue}>{formatMoney(deliveryFeeAmount)}</span>
              </div>

              <div className={styles.receiptDividerSolid} />

              <div className={styles.receiptGrandRow}>
                <span className={styles.receiptGrandLabel}>Total</span>
                <span className={styles.receiptGrandValue}>{formatMoney(orderGrandTotal)}</span>
              </div>

              <div className={styles.receiptNote}>
                Prices shown are for the selected items only. Unselected items remain in your cart.
              </div>
            </div>

            <div className={styles.receiptDividerDotted} />

            <div className={styles.receiptFooter}>
              <div className={styles.receiptFooterLine}>
                Need help? Contact support from the Contact Us page.
              </div>
              <div className={styles.receiptFooterLineMuted}>
                By placing your order, you agree to our terms and return policy.
              </div>
            </div>
          </div>

          <div className={styles.receiptTearBottom} aria-hidden="true" />
        </div>
      </div>
    </section>
  );
};

export default CheckoutSummarySection;
